import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";

export const Hero = ({ t, lang, scrollTo }) => {
  const { scrollY } = useScroll();
  const yGhost = useTransform(scrollY, [0, 700], [0, 160]);
  const yBadge = useTransform(scrollY, [0, 700], [0, -60]);

  return (
    <section id="top" data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden bg-[#F1F2F4]">
      <div className="absolute inset-0 hero-grid" />
      <div className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-[#C5A059]/20 blur-[140px]" />
      <motion.div
        style={{ y: yGhost }}
        aria-hidden
        className="pointer-events-none absolute -right-10 top-1/2 -translate-y-1/2 font-display italic text-[26rem] leading-none text-[#0B132B]/[0.05] select-none hidden lg:block"
      >
        BA
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-6 sm:px-10 pt-32 pb-24 w-full">
        <motion.p
          key={`label-${lang}`}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          data-testid="hero-label"
          className="font-mono2 text-xs uppercase tracking-[0.35em] text-[#8C713A] mb-10 flex items-center gap-4"
        >
          <span className="inline-block h-px w-10 bg-[#8C713A]" />
          {t.hero.label}
        </motion.p>

        <h1 data-testid="hero-headline" className="font-display font-semibold tracking-tight leading-[0.95] text-6xl sm:text-7xl lg:text-8xl text-[#0B132B]">
          {t.hero.lines.map((line, i) => (
            <span key={`${lang}-${i}`} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 1 ? "italic text-[#8C713A]" : ""}`}
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.1, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-12 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-12">
          <motion.p
            key={`sub-${lang}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85 }}
            data-testid="hero-sub"
            className="max-w-xl text-base sm:text-lg font-light text-slate-600 leading-relaxed"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1 }}
            className="flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-primary"
              onClick={() => scrollTo("#contact")}
              className="group inline-flex items-center gap-3 rounded-full bg-[#0B132B] text-white font-medium px-7 py-3.5 hover:bg-[#C5A059] hover:text-[#0B132B] transition-colors"
            >
              {t.hero.ctaPrimary}
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => scrollTo("#services")}
              className="inline-flex items-center gap-3 rounded-full border border-[#0B132B]/25 text-[#0B132B] px-7 py-3.5 hover:border-[#C5A059] hover:bg-[#C5A059]/10 transition-colors"
            >
              {t.hero.ctaSecondary}
            </button>
          </motion.div>
        </div>
      </div>

      <motion.div
        style={{ y: yBadge }}
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1, delay: 1.15 }}
        data-testid="hero-badge"
        className="absolute bottom-24 right-8 sm:right-16 flex flex-col items-center justify-center w-32 h-32 sm:w-40 sm:h-40 rounded-full border border-[#C5A059]/60 bg-white/80 backdrop-blur-md shadow-sm"
      >
        <span className="font-display text-3xl sm:text-4xl font-semibold text-[#8C713A]">{t.hero.badge}</span>
        <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-500 text-center px-3 mt-1">{t.hero.badgeText}</span>
      </motion.div>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        onClick={() => scrollTo("#about")}
        data-testid="hero-scroll-hint"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-slate-400 hover:text-[#8C713A] transition-colors"
      >
        <span className="font-mono2 text-[10px] uppercase tracking-[0.3em]">{t.hero.scroll}</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
          <ArrowDown size={16} />
        </motion.span>
      </motion.button>
    </section>
  );
};
